import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import {
	isMarketUnavailable,
	useBars,
	useFundamentals,
	useQuote,
	useTicker,
	type ChartRange,
} from "../../lib/api/market";
import { listMyFams, getFamBySlug } from "../../lib/api";
import { StockChart } from "../../components/StockChart";
import type { FamDetail, FamSummary } from "../../lib/types";

// /stock/:symbol — Standalone stock detail page. Reachable from any Fam, the
// dashboard, or (eventually) the propose form. Two display planes:
//
//   1. Live data — quote, chart, fundamentals. Driven by hedgefam-market-data.
//      When the upstream is offline (e.g. POLYGON_API_KEY not yet set), these
//      sections degrade gracefully to a "Live market data is paused" banner;
//      the page does NOT crash.
//   2. Reference + cross-Fam context — ticker name, exchange, and a list of
//      the user's Fams that hold this symbol in their composite. Independent
//      of Polygon, so always renders.

const RANGES: ChartRange[] = ["1D", "1W", "1M", "3M", "1Y", "5Y"];
const DEFAULT_RANGE: ChartRange = "1M";

export function StockDetailRoute() {
	const params = useParams<{ symbol: string }>();
	const rawSymbol = params.symbol ?? "";
	const symbol = rawSymbol.trim().toUpperCase();
	const [range, setRange] = useState<ChartRange>(DEFAULT_RANGE);

	const quoteQuery = useQuote(symbol);
	const barsQuery = useBars(symbol, range);
	const tickerQuery = useTicker(symbol);
	const fundamentalsQuery = useFundamentals(symbol);

	const quoteUnavailable = isMarketUnavailable(quoteQuery.error);
	const barsUnavailable = isMarketUnavailable(barsQuery.error);

	if (symbol.length === 0) {
		return (
			<div className="mx-auto max-w-3xl px-4 py-8 text-center text-sm text-[var(--color-muted)]">
				Invalid stock symbol.
			</div>
		);
	}

	const displayName = tickerQuery.data?.name ?? null;
	const exchange = tickerQuery.data?.exchange ?? null;
	const marketCap = tickerQuery.data?.marketCapUsd ?? null;

	return (
		<div className="mx-auto max-w-5xl space-y-6 px-4 py-8">
			{/* --- Header ------------------------------------------------- */}
			<header>
				<Link
					to="/"
					className="text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)]"
				>
					← Dashboard
				</Link>
				<div className="mt-2 flex items-start justify-between gap-4">
					<div>
						<h1 className="font-mono text-2xl font-bold tracking-tight">
							{bareSymbol(symbol)}
						</h1>
						<p className="text-sm text-[var(--color-muted)]">
							{displayName ??
								(tickerQuery.isLoading ? "Loading…" : "Unknown ticker")}
							{exchange ? ` · ${exchange}` : ""}
						</p>
					</div>
					<div className="text-right">
						{quoteUnavailable ? (
							<p className="text-xs text-[var(--color-warning)]">
								Live market data is paused.
							</p>
						) : quoteQuery.data ? (
							<>
								<p className="font-mono text-2xl font-semibold tabular-nums">
									{formatPrice(quoteQuery.data.last)}
								</p>
								<p
									className={[
										"text-sm tabular-nums",
										quoteQuery.data.changePct >= 0
											? "text-[var(--color-accent)]"
											: "text-[var(--color-danger)]",
									].join(" ")}
								>
									{formatPctSigned(quoteQuery.data.changePct)} today
								</p>
							</>
						) : (
							<p className="text-sm text-[var(--color-muted)]">—</p>
						)}
						{marketCap !== null && marketCap > 0 && (
							<p className="mt-1 text-[11px] text-[var(--color-muted)]">
								Market cap: {formatLargeUsd(marketCap)}
							</p>
						)}
					</div>
				</div>
			</header>

			{/* --- Range tabs + chart ------------------------------------ */}
			<section className="space-y-3">
				<div className="flex items-center gap-1">
					{RANGES.map((r) => (
						<button
							key={r}
							type="button"
							onClick={() => setRange(r)}
							className={[
								"rounded-md px-3 py-1.5 text-xs font-semibold transition",
								r === range
									? "bg-[var(--color-accent-soft)] text-[var(--color-fg)]"
									: "text-[var(--color-muted)] hover:text-[var(--color-fg)]",
							].join(" ")}
							aria-pressed={r === range}
						>
							{r}
						</button>
					))}
				</div>

				{barsUnavailable ? (
					<div className="flex h-[280px] items-center justify-center rounded-md border border-dashed border-[var(--color-border)] bg-[var(--color-bg)] text-xs text-[var(--color-muted)]">
						Market data temporarily unavailable.
					</div>
				) : (
					<StockChart
						bars={barsQuery.data?.bars ?? []}
						range={range}
						loading={barsQuery.isLoading}
					/>
				)}
			</section>

			{/* --- Fundamentals + Where it shows up ---------------------- */}
			<section className="grid gap-4 md:grid-cols-2">
				<FundamentalsCard
					data={fundamentalsQuery.data}
					isLoading={fundamentalsQuery.isLoading}
					unavailable={isMarketUnavailable(fundamentalsQuery.error)}
				/>
				<WhereItShowsUpCard symbol={symbol} />
			</section>

			<footer className="border-t border-[var(--color-border)] pt-4 text-center text-xs text-[var(--color-muted)]">
				Simulated paper trade. Not investment advice.
			</footer>
		</div>
	);
}

// --- Fundamentals -----------------------------------------------------------

interface FundamentalsCardProps {
	data:
		| {
				peRatio: number;
				dividendYield: number;
				marketCap: number;
				sharesOutstanding: number;
				sector: string;
		  }
		| undefined;
	isLoading: boolean;
	unavailable: boolean;
}

function FundamentalsCard({ data, isLoading, unavailable }: FundamentalsCardProps) {
	return (
		<div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
			<h2 className="text-sm font-semibold">Fundamentals</h2>
			{unavailable ? (
				<p className="mt-3 text-xs text-[var(--color-muted)]">
					Fundamentals temporarily unavailable.
				</p>
			) : isLoading ? (
				<p className="mt-3 text-xs text-[var(--color-muted)]">Loading…</p>
			) : !data ? (
				<p className="mt-3 text-xs text-[var(--color-muted)]">No data.</p>
			) : (
				<dl className="mt-3 grid grid-cols-2 gap-3 text-xs">
					<FundamentalRow label="P/E" value={formatNumber(data.peRatio)} />
					<FundamentalRow
						label="Div yield"
						value={formatPct(data.dividendYield * 100)}
					/>
					<FundamentalRow
						label="Market cap"
						value={data.marketCap > 0 ? formatLargeUsd(data.marketCap) : "—"}
					/>
					<FundamentalRow label="Sector" value={data.sector || "—"} />
				</dl>
			)}
		</div>
	);
}

function FundamentalRow({ label, value }: { label: string; value: string }) {
	return (
		<div>
			<dt className="text-[10px] uppercase tracking-wider text-[var(--color-muted)]">
				{label}
			</dt>
			<dd className="mt-0.5 text-sm font-semibold tabular-nums">{value}</dd>
		</div>
	);
}

// --- Where it shows up ------------------------------------------------------

interface FamMention {
	slug: string;
	name: string;
	allocationPct: number;
}

function WhereItShowsUpCard({ symbol }: { symbol: string }) {
	const famsQuery = useQuery({
		queryKey: ["me", "fams"],
		queryFn: listMyFams,
	});

	// Hydrate composites for each fam. The summary endpoint doesn't return
	// the composite so we have to fan out. Cheap — each fam is a single
	// service-binding hop with the FAM Worker.
	const fams = famsQuery.data ?? [];
	const compositesQuery = useQuery({
		queryKey: ["me", "fam-composites", fams.map((f: FamSummary) => f.slug).sort()],
		queryFn: async () => {
			const detailed = await Promise.all(
				fams.map((f) =>
					getFamBySlug(f.slug)
						.then((d: FamDetail) => d)
						.catch(() => null),
				),
			);
			return detailed.filter((d): d is FamDetail => d !== null);
		},
		enabled: fams.length > 0,
		staleTime: 30_000,
	});

	const mentions: FamMention[] = useMemo(() => {
		const list: FamMention[] = [];
		const details = compositesQuery.data ?? [];
		for (const d of details) {
			// Composite keys are full canonical symbols ("NASDAQ:AAPL"). The
			// detail route is keyed on the bare ticker too, so match against
			// both the exact key and the suffix after ":".
			for (const [sym, alloc] of Object.entries(d.composite)) {
				if (
					sym === symbol ||
					sym.toUpperCase() === symbol ||
					sym.split(":").pop()?.toUpperCase() === bareSymbol(symbol)
				) {
					list.push({ slug: d.slug, name: d.name, allocationPct: alloc });
				}
			}
		}
		// Sort by largest allocation first — PMs care about their biggest
		// exposure most.
		return list.sort((a, b) => b.allocationPct - a.allocationPct);
	}, [compositesQuery.data, symbol]);

	return (
		<div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
			<h2 className="text-sm font-semibold">Where it shows up</h2>
			{famsQuery.isLoading || compositesQuery.isLoading ? (
				<p className="mt-3 text-xs text-[var(--color-muted)]">Loading…</p>
			) : mentions.length === 0 ? (
				<p className="mt-3 text-xs text-[var(--color-muted)]">
					Not held by any of your Fams.
				</p>
			) : (
				<ul className="mt-3 space-y-2">
					{mentions.map((m) => (
						<li key={m.slug}>
							<Link
								to={`/fam/${m.slug}`}
								className="flex items-center justify-between rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-xs hover:border-[var(--color-accent)]"
							>
								<span className="font-semibold">{m.name}</span>
								<span className="font-mono tabular-nums text-[var(--color-muted)]">
									{m.allocationPct.toFixed(2)}%
								</span>
							</Link>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

// --- formatting helpers -----------------------------------------------------

function bareSymbol(s: string): string {
	const last = s.split(":").pop();
	return (last ?? s).toUpperCase();
}

function formatPrice(n: number): string {
	if (!Number.isFinite(n)) return "—";
	return `$${n.toFixed(2)}`;
}

function formatNumber(n: number): string {
	if (!Number.isFinite(n) || n === 0) return "—";
	return n.toFixed(2);
}

function formatPct(n: number): string {
	if (!Number.isFinite(n)) return "—";
	return `${n.toFixed(2)}%`;
}

function formatPctSigned(n: number): string {
	if (!Number.isFinite(n)) return "—";
	const sign = n > 0 ? "+" : "";
	return `${sign}${n.toFixed(2)}%`;
}

function formatLargeUsd(n: number): string {
	if (!Number.isFinite(n) || n <= 0) return "—";
	if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
	if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
	if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
	return `$${n.toFixed(0)}`;
}
